import React, { useState, useMemo, useEffect } from 'react';
import { Badge, Dropdown, Button, Space, Typography } from 'antd';
import { 
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { FaHandshake } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AgreementUpdateModal from './AgreementUpdateModal';

const { Text } = Typography;

const DataAgreementNotification = ({ userInfo: initialUserInfo }) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [userInfo, setUserInfo] = useState(initialUserInfo);

  // Listen to Redux state updates
  const businessOrganisationUpdate = useSelector(state => state.businessOrganisationUpdate);
  const { success: successUpdate, businessOrganisation: updatedOrganisation } = businessOrganisationUpdate;

  // Update userInfo when Redux state changes (successful update)
  useEffect(() => {
    if (successUpdate && updatedOrganisation && userInfo?.businessOrganisationId === updatedOrganisation.id) {
      const updatedUserInfo = {
        ...userInfo,
        dsaStatus: updatedOrganisation.dsaStatus,
        dpaStatus: updatedOrganisation.dpaStatus,
        tocStatus: updatedOrganisation.tocStatus,
        dsaSignedDate: updatedOrganisation.dsaSignedDate,
        dpaSignedDate: updatedOrganisation.dpaSignedDate,
        tocSignedDate: updatedOrganisation.tocSignedDate,
        dsaSignedBy: updatedOrganisation.dsaSignedBy,
        dpaSignedBy: updatedOrganisation.dpaSignedBy,
        tocSignedBy: updatedOrganisation.tocSignedBy
      };
      setUserInfo(updatedUserInfo);
      
      // Also update localStorage to keep it in sync
      localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
      
      // Dispatch custom event to notify other components
      window.dispatchEvent(new CustomEvent('userInfoUpdated', { detail: updatedUserInfo }));
    }
  }, [successUpdate, updatedOrganisation, userInfo?.businessOrganisationId]);

  // Listen to localStorage changes (for cross-tab updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Only handle userInfo changes
      if (e.key === 'userInfo') {
        const freshUserInfo = JSON.parse(e.newValue || '{}');
        if (freshUserInfo && freshUserInfo.businessOrganisationId === userInfo?.businessOrganisationId) {
          setUserInfo(freshUserInfo);
        }
      }
    };

    // Handle custom userInfo update events (same-tab updates)
    const handleUserInfoUpdate = (e) => {
      const freshUserInfo = e.detail;
      if (freshUserInfo && freshUserInfo.businessOrganisationId === userInfo?.businessOrganisationId) {
        setUserInfo(freshUserInfo);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userInfoUpdated', handleUserInfoUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userInfoUpdated', handleUserInfoUpdate);
    };
  }, [userInfo?.businessOrganisationId]);

  // Update userInfo when initialUserInfo prop changes
  useEffect(() => {
    setUserInfo(initialUserInfo);
  }, [initialUserInfo]);

  // Calculate agreement statuses
  const agreementStatuses = useMemo(() => {
    if (!userInfo) return { dsa: 'NotSigned', dpa: 'NotSigned', toc: 'NotSigned' };
    
    return {
      dsa: userInfo.dsaStatus || 'NotSigned',
      dpa: userInfo.dpaStatus || 'NotSigned',
      toc: userInfo.tocStatus || 'NotSigned'
    };
  }, [userInfo]);

  // Calculate notification count
  const notificationCount = useMemo(() => {
    const statuses = Object.values(agreementStatuses);
    return statuses.filter(status => status === 'NotSigned' || status === 'Pending').length;
  }, [agreementStatuses]);

  // Get status icon and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'Signed':
        return { icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />, color: 'success' };
      case 'Pending':
        return { icon: <ClockCircleOutlined style={{ color: '#faad14' }} />, color: 'warning' };
      case 'InProgress':
        return { icon: <ClockCircleOutlined style={{ color: '#1890ff' }} />, color: 'processing' };
      case 'NotSigned':
        return { icon: <CloseCircleOutlined style={{ color: '#f5222d' }} />, color: 'error' };
      default:
        return { icon: <CloseCircleOutlined style={{ color: '#8c8c8c' }} />, color: 'default' };
    }
  };

  // Handle agreement click
  const handleAgreementClick = (agreementType) => {
    if (!userInfo?.businessOrganisationId) {
      return;
    }

    setSelectedAgreement(agreementType);
    setModalVisible(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedAgreement(null);
  };

  // Dropdown menu items
  const menuItems = [
    {
      key: 'dsa',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Space>
            {getStatusInfo(agreementStatuses.dsa).icon}
            <Text style={{ fontSize: '13px' }}>
              {t('Data Sharing Agreement (DSA)')}
            </Text>
          </Space>
        </div>
      ),
      onClick: () => handleAgreementClick('dsa')
    },
    {
      key: 'dpa',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Space>
            {getStatusInfo(agreementStatuses.dpa).icon}
            <Text style={{ fontSize: '13px' }}>
              {t('Data Processing Agreement (DPA)')}
            </Text>
          </Space>
        </div>
      ),
      onClick: () => handleAgreementClick('dpa')
    },
    {
      key: 'toc',
      label: (
        <div style={{ padding: '4px 0' }}>
          <Space>
            {getStatusInfo(agreementStatuses.toc).icon}
            <Text style={{ fontSize: '13px' }}>
              {t('Terms of Contract (TOC)')}
            </Text>
          </Space>
        </div>
      ),
      onClick: () => handleAgreementClick('toc')
    }
  ];

  return (
    <>
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={['click']}
      >
        <Button
          type="text"
          icon={<FaHandshake />}
          style={{ 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            padding: '4px 12px',
            height: '32px',
            width: '32px',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
        >
          {notificationCount === 0 ? (
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
          ) : (
            <Badge 
              count={notificationCount} 
              size="small"
              style={{ 
                backgroundColor: '#f5222d',
                fontSize: '10px',
                minWidth: '16px',
                height: '16px',
                lineHeight: '16px'
              }}
            />
          )}
        </Button>
      </Dropdown>

      {/* Agreement Update Modal */}
      <AgreementUpdateModal
        visible={modalVisible}
        onCancel={handleModalClose}
        organisationId={userInfo?.businessOrganisationId}
        selectedAgreement={selectedAgreement}
      />
    </>
  );
};

export default DataAgreementNotification;
